import { useEffect, useState } from 'react';
import Reveal from '../../components/Reveal';
import HeroSlideshow from '../../components/HeroSlideshow';
import SafeImage from '../../components/SafeImage';
import './manyara.css';
import { IMAGE_FALLBACK, MANYARA_HERO_SLIDES, MANYARA_PAGE_IMAGES } from '../../data/destinationImages';

import lionFace from '../../assets/images/lion-face.jpg';
import elephantFace from '../../assets/images/elephant-face.jpg';
import eagleFace from '../../assets/images/eagle-face.jpg';
import wildebeestFace from '../../assets/images/wildebeest-face.jpg';
import rhinoFace from '../../assets/images/rhino-face.jpg';
import wilddogFace from '../../assets/images/wilddog-face.jpg';

const MANYARA_IMAGES = MANYARA_PAGE_IMAGES;

const HERO_STATS = [
  { value: '330', label: 'km² park area', delay: 0 },
  { value: '400+', label: 'Bird species', delay: 80 },
  { value: 'Iconic', label: 'Tree-climbing lions', delay: 160 },
  { value: '1960', label: 'Established', delay: 240 },
];

const WILDLIFE = [
  {
    animalFace: lionFace,
    title: 'Tree-Climbing Lions',
    text: 'Lake Manyara is famous for lions that rest on acacia branches a rare behaviour that makes every sighting memorable.',
    facePosition: 'center 38%',
  },
  {
    animalFace: eagleFace,
    title: 'Flamingos & Waterbirds',
    text: 'Thousands of flamingos gather on the alkaline lake, alongside pelicans, storks, cormorants, and over 400 bird species.',
    facePosition: 'center 28%',
  },
  {
    animalFace: elephantFace,
    title: 'Elephants & Buffalo',
    text: 'Large mammals move through groundwater forest and open plains elephants, buffalo, and giraffes are commonly seen.',
    facePosition: 'center 42%',
  },
  {
    animalFace: wildebeestFace,
    title: 'Hippos & Baboons',
    text: 'Hippo pools hide in the lake margins while olive baboon troops forage along the forest edge lively and photogenic.',
    facePosition: 'center 35%',
  },
  {
    animalFace: rhinoFace,
    title: 'Leopards & Hyenas',
    text: 'Leopards slip through the forest canopy at dusk, while spotted hyenas patrol the lake shore after dark.',
    facePosition: 'center 40%',
  },
  {
    animalFace: wilddogFace,
    title: 'Forest Specialists',
    text: 'Blue monkeys, hornbills, and silvery-cheeked hornbills thrive in the park\'s lush groundwater forest corridor.',
    facePosition: 'center 36%',
  },
];

const SEASONS = [
  {
    badge: 'peak',
    title: 'Dry Season',
    months: 'June – October',
    text: 'Wildlife concentrates around remaining water as the lake recedes excellent big-game viewing and clear Rift Valley views.',
  },
  {
    badge: 'green',
    title: 'Green Season',
    months: 'November – March',
    text: 'The forest is at its lushest, migratory birds arrive in numbers, and flamingo flocks can carpet the lake in pink.',
  },
  {
    badge: 'shoulder',
    title: 'Shoulder Season',
    months: 'April – May',
    text: 'Long rains bring fewer visitors and emerald landscapes. Some tracks are muddy, but the park feels wonderfully private.',
  },
];

const EXPERIENCES = [
  {
    image: MANYARA_IMAGES.lake,
    title: 'Lake Shore Game Drives',
    text: 'Follow the lakeside track for flamingo flocks, hippo pools, and wide Rift Valley panoramas the park\'s signature route.',
  },
  {
    image: MANYARA_IMAGES.forest,
    title: 'Groundwater Forest',
    text: 'Drive beneath fig and mahogany canopy where elephants, baboons, and hornbills share the shaded forest trails.',
  },
  {
    image: MANYARA_IMAGES.escarpment,
    title: 'Rift Valley Escarpment',
    text: 'Climb to viewpoints above the lake for sweeping views across the Great Rift Valley a perfect Northern Circuit stopover.',
  },
];

const GALLERY_IMAGES = [
  { src: MANYARA_IMAGES.hero, caption: 'Lake Manyara' },
  { src: MANYARA_IMAGES.wildlife, caption: 'Flamingo Flocks' },
  { src: MANYARA_IMAGES.lake, caption: 'Alkaline Lake' },
  { src: MANYARA_IMAGES.forest, caption: 'Groundwater Forest' },
  { src: MANYARA_IMAGES.riftValley, caption: 'Rift Valley Views' },
  { src: MANYARA_IMAGES.escarpment, caption: 'Great Rift Escarpment' },
];

const SAFARI_PACKAGES = [
  {
    image: MANYARA_IMAGES.lake,
    title: 'Manyara Half-Day Safari',
    duration: '½–1 day',
    tag: 'Day trip',
    text: 'A compact game drive from Arusha perfect for flamingos, forest wildlife, and Rift Valley viewpoints en route north.',
    highlights: ['Arusha pickup', 'Lake shore drive', 'Forest loop'],
  },
  {
    image: MANYARA_IMAGES.forest,
    title: 'Manyara Overnight Escape',
    duration: '2 days',
    tag: 'Short safari',
    text: 'Stay near the park gate for sunset lake views and a full morning among elephants, baboons, and birdlife.',
    highlights: ['Lodge near Mto wa Mbu', 'Sunrise drive', 'Village / cultural option'],
  },
  {
    image: MANYARA_IMAGES.wildlife,
    title: 'Manyara & Tarangire Combo',
    duration: '3–4 days',
    tag: 'Classic combo',
    text: 'Pair Lake Manyara\'s groundwater forest with Tarangire\'s elephants and baobabs two contrasting parks in one trip.',
    highlights: ['Two parks', 'Birdlife focus', 'Escarpment views'],
  },
  {
    image: MANYARA_IMAGES.escarpment,
    title: 'Northern Circuit Stopover',
    duration: '5–7 days',
    tag: 'Most popular',
    text: 'Open with Tarangire and Manyara, then continue to Ngorongoro Crater and the Serengeti the classic northern route.',
    highlights: ['Multi-park itinerary', 'Private 4×4', 'Flexible lodges'],
  },
];

const GETTING_THERE_ROUTES = [
  'Drive from Arusha (approx. 1.5–2 hrs) ideal half-day or en-route to Ngorongoro',
  'Combine with Tarangire, Ngorongoro, and Serengeti on a classic Northern Circuit safari',
  'Fly into Kilimanjaro International Airport (JRO), then road transfer via Arusha',
  'Allow at least half a day the main lake circuit and forest loop reward unhurried pacing',
];

function Lightbox({ image, onClose }) {
  if (!image) return null;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div
      className="manyara-lightbox"
      role="dialog"
      aria-modal="true"
      aria-label="Image viewer"
      onClick={handleBackdropClick}
    >
      <button
        className="manyara-lightbox__close"
        aria-label="Close image viewer"
        onClick={onClose}
      >
        ✕
      </button>
      <img
        className="manyara-lightbox__img"
        src={image.src}
        alt={image.caption}
      />
      <span className="manyara-lightbox__caption">{image.caption}</span>
    </div>
  );
}

function Manyara() {
  const [lightboxImage, setLightboxImage] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="manyara">
      <section className="manyara-hero" aria-labelledby="manyara-hero-heading">
        <div className="manyara-hero__media" aria-hidden="true">
          <HeroSlideshow classPrefix="manyara-hero" slides={MANYARA_HERO_SLIDES} fallback={IMAGE_FALLBACK} />
          <div className="manyara-hero__overlay" />
        </div>
        <div className="page-container manyara-hero__container">
          <div className="manyara-hero__content">
            <nav className="manyara-hero__breadcrumb" aria-label="Breadcrumb">
              <a href="/">Home</a>
              <span className="manyara-hero__breadcrumb-sep" aria-hidden="true">›</span>
              <a href="#safaris">Northern Circuit</a>
              <span className="manyara-hero__breadcrumb-sep" aria-hidden="true">›</span>
              <span>Lake Manyara</span>
            </nav>
            <p className="manyara-hero__eyebrow">Northern Safari Circuit</p>
            <h1 id="manyara-hero-heading">
              Lake Manyara
              <span>National Park</span>
            </h1>
            <p className="manyara-hero__lead">
              Tree-climbing lions, flamingo-filled waters, and a lush groundwater
              forest tucked beneath the Rift Valley escarpment compact, diverse,
              and unforgettable.
            </p>
            <div className="manyara-hero__actions">
              <a className="manyara-btn manyara-btn--primary" href="#contact">
                Plan your Manyara safari
              </a>
              <a className="manyara-btn manyara-btn--outline" href="#manyara-gallery">
                View gallery
              </a>
            </div>
          </div>
        </div>
        <div className="manyara-hero__stats">
          {HERO_STATS.map((stat) => (
            <div
              key={stat.label}
              className="manyara-hero__stat"
              style={{ '--stat-delay': `${stat.delay}ms` }}
            >
              <span className="manyara-hero__stat-value">{stat.value}</span>
              <span className="manyara-hero__stat-label">{stat.label}</span>
            </div>
          ))}
        </div>
      </section>

      <section
        id="manyara-overview"
        className="manyara-section manyara-overview"
        aria-labelledby="overview-heading"
      >
        <div className="page-container">
          <div className="manyara-overview__grid">
            <Reveal className="manyara-overview__text">
              <p className="manyara-section__eyebrow">About the park</p>
              <h2 id="overview-heading">A jewel of the Rift Valley</h2>
              <p>
                Lake Manyara National Park covers the northwestern shore of its
                namesake alkaline lake, hemmed in by the dramatic wall of the Great
                Rift Valley. Ernest Hemingway called it &ldquo;the loveliest lake
                in Africa&rdquo; a compact park that packs forest, lake, and
                escarpment into a single unforgettable drive.
              </p>
              <p>
                Despite its modest size, Manyara delivers extraordinary variety:
                tree-climbing lions, vast flamingo congregations, elephant herds
                in mahogany forest, and some of East Africa&apos;s richest birdlife.
                It is the perfect half-day or overnight stop between Arusha and
                Ngorongoro on any Northern Circuit itinerary.
              </p>
              <ul className="manyara-overview__details">
                <Reveal as="li" className="manyara-overview__detail" delay={0}>
                  <span className="manyara-overview__detail-label">Location</span>
                  <span className="manyara-overview__detail-value">Great Rift Valley</span>
                </Reveal>
                <Reveal as="li" className="manyara-overview__detail" delay={80}>
                  <span className="manyara-overview__detail-label">Park size</span>
                  <span className="manyara-overview__detail-value">330 km²</span>
                </Reveal>
                <Reveal as="li" className="manyara-overview__detail" delay={160}>
                  <span className="manyara-overview__detail-label">Established</span>
                  <span className="manyara-overview__detail-value">1960</span>
                </Reveal>
                <Reveal as="li" className="manyara-overview__detail" delay={240}>
                  <span className="manyara-overview__detail-label">Best for</span>
                  <span className="manyara-overview__detail-value">Birds &amp; lions</span>
                </Reveal>
              </ul>
            </Reveal>
            <Reveal className="manyara-overview__image" variant="right" delay={200}>
              <SafeImage
                className="manyara-overview__img"
                src={MANYARA_IMAGES.overview}
                alt="Lioness resting in a tree at Lake Manyara National Park"
              />
              <span className="manyara-overview__image-accent" aria-hidden="true" />
            </Reveal>
          </div>
        </div>
      </section>

      {/* ──── Safari packages ──── */}
      <section
        id="manyara-packages"
        className="manyara-section manyara-packages"
        aria-labelledby="packages-heading"
      >
        <div className="page-container">
          <Reveal className="manyara-section__header">
            <p className="manyara-section__eyebrow">Safari packages</p>
            <h2 id="packages-heading">Lake Manyara safari packages</h2>
            <p className="manyara-section__intro">
              From a half-day stopover to a full Northern Circuit journey each
              package is tailored to your dates, lodge style, and travel pace.
            </p>
          </Reveal>
          <ul className="manyara-packages__grid">
            {SAFARI_PACKAGES.map((pkg, index) => (
              <Reveal
                key={pkg.title}
                as="li"
                className="manyara-packages__card"
                delay={index * 100}
              >
                <div className="manyara-packages__card-image">
                  <SafeImage src={pkg.image} alt={pkg.title} />
                  <span className="manyara-packages__duration">{pkg.duration}</span>
                </div>
                <div className="manyara-packages__card-body">
                  <span className="manyara-packages__tag">{pkg.tag}</span>
                  <h3>{pkg.title}</h3>
                  <p>{pkg.text}</p>
                  <ul className="manyara-packages__highlights">
                    {pkg.highlights.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                  <a className="manyara-packages__cta" href="#contact">
                    Enquire about this package →
                  </a>
                </div>
              </Reveal>
            ))}
          </ul>
        </div>
      </section>

      <section
        id="manyara-wildlife"
        className="manyara-section manyara-wildlife"
        aria-labelledby="wildlife-heading"
      >
        <div className="page-container">
          <Reveal className="manyara-section__header">
            <p className="manyara-section__eyebrow">Iconic wildlife</p>
            <h2 id="wildlife-heading">Forest, lake &amp; escarpment</h2>
            <p className="manyara-section__intro">
              From acacia-roosting lions to pink flamingo clouds Lake Manyara
              squeezes an astonishing range of habitats and species into one
              compact park.
            </p>
          </Reveal>
          <ul className="manyara-wildlife__grid">
            {WILDLIFE.map((item, index) => (
              <Reveal
                key={item.title}
                as="li"
                className="manyara-wildlife__card"
                delay={index * 100}
                variant="scale"
              >
                <div className="manyara-wildlife__icon-frame">
                  <span className="manyara-wildlife__icon-ring" aria-hidden="true" />
                  <span
                    className="manyara-wildlife__icon"
                    style={{ '--wildlife-face-position': item.facePosition }}
                  >
                    <img src={item.animalFace} alt={item.title} />
                  </span>
                </div>
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </Reveal>
            ))}
          </ul>
        </div>
      </section>

      <section
        id="manyara-gallery"
        className="manyara-section manyara-gallery"
        aria-labelledby="gallery-heading"
      >
        <div className="page-container">
          <Reveal className="manyara-section__header">
            <p className="manyara-section__eyebrow">Photo gallery</p>
            <h2 id="gallery-heading">Scenes from Lake Manyara</h2>
            <p className="manyara-section__intro">
              From flamingo-pink shorelines to misty forest trails the many
              faces of one of Tanzania&apos;s most diverse small parks.
            </p>
          </Reveal>
          <div className="manyara-gallery__grid">
            {GALLERY_IMAGES.map((img, index) => (
              <Reveal
                key={img.caption}
                className="manyara-gallery__item"
                delay={index * 100}
                variant="scale"
                onClick={() => setLightboxImage(img)}
                role="button"
                tabIndex={0}
                aria-label={`View ${img.caption}`}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setLightboxImage(img);
                  }
                }}
              >
                <SafeImage src={img.src} alt={img.caption} />
                <span className="manyara-gallery__caption">{img.caption}</span>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {lightboxImage && (
        <Lightbox image={lightboxImage} onClose={() => setLightboxImage(null)} />
      )}

      <section
        id="manyara-seasons"
        className="manyara-section manyara-seasons"
        aria-labelledby="seasons-heading"
      >
        <div className="page-container">
          <Reveal className="manyara-section__header">
            <p className="manyara-section__eyebrow">Plan your timing</p>
            <h2 id="seasons-heading">Best time to visit Lake Manyara</h2>
            <p className="manyara-section__intro">
              Manyara rewards visitors year-round dry months concentrate
              wildlife, while green season brings peak birdlife and flamingo
              spectacles.
            </p>
          </Reveal>
          <ul className="manyara-seasons__grid">
            {SEASONS.map((season, index) => (
              <Reveal
                key={season.title}
                as="li"
                className="manyara-seasons__card"
                delay={index * 120}
              >
                <span
                  className={`manyara-seasons__card-badge manyara-seasons__card-badge--${season.badge}`}
                >
                  {season.badge === 'peak'
                    ? '★ Peak Season'
                    : season.badge === 'green'
                      ? '🌿 Green Season'
                      : '◐ Shoulder Season'}
                </span>
                <h3>{season.title}</h3>
                <span className="manyara-seasons__card-months">{season.months}</span>
                <p>{season.text}</p>
              </Reveal>
            ))}
          </ul>
        </div>
      </section>

      <section
        id="manyara-experiences"
        className="manyara-section manyara-experiences"
        aria-labelledby="experiences-heading"
      >
        <div className="page-container">
          <Reveal className="manyara-section__header">
            <p className="manyara-section__eyebrow">Safari experiences</p>
            <h2 id="experiences-heading">Ways to explore Manyara</h2>
            <p className="manyara-section__intro">
              A single day can cover lake, forest, and escarpment or linger
              longer to savour one of the Northern Circuit&apos;s most varied
              compact parks.
            </p>
          </Reveal>
          <ul className="manyara-experiences__grid">
            {EXPERIENCES.map((exp, index) => (
              <Reveal
                key={exp.title}
                as="li"
                className="manyara-experiences__card"
                delay={index * 120}
              >
                <div className="manyara-experiences__card-image">
                  <SafeImage src={exp.image} alt={exp.title} />
                </div>
                <div className="manyara-experiences__card-body">
                  <h3>{exp.title}</h3>
                  <p>{exp.text}</p>
                </div>
              </Reveal>
            ))}
          </ul>
        </div>
      </section>

      <section
        id="manyara-getting-there"
        className="manyara-section manyara-getting-there"
        aria-labelledby="getting-there-heading"
      >
        <div className="page-container">
          <div className="manyara-getting-there__inner">
            <Reveal className="manyara-getting-there__copy">
              <p className="manyara-section__eyebrow">Getting there</p>
              <h2 id="getting-there-heading">How to reach Lake Manyara</h2>
              <p>
                Lake Manyara lies on the main road between Arusha and
                Ngorongoro, roughly 126 km west of Arusha. Most Northern
                Circuit safaris pass through here it is one of the easiest
                parks to combine with Tarangire or the crater.
              </p>
              <ul className="manyara-getting-there__routes">
                {GETTING_THERE_ROUTES.map((route) => (
                  <li key={route}>{route}</li>
                ))}
              </ul>
            </Reveal>
            <Reveal className="manyara-getting-there__map" variant="right" delay={200}>
              <iframe
                title="Lake Manyara National Park location"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d255168!2d35.8!3d-3.5!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x1837193a8f0e3b1d%3A0x6b8c8e8e8e8e8e8e!2sLake%20Manyara%20National%20Park!5e0!3m2!1sen!2stz!4v1700000000000!5m2!1sen!2stz"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </Reveal>
          </div>
        </div>
      </section>

      <section className="manyara-cta" aria-labelledby="manyara-cta-heading">
        <div className="page-container">
          <Reveal variant="scale" delay={0}>
            <div className="manyara-cta__inner">
              <h2 id="manyara-cta-heading">
                Ready to explore the Rift Valley jewel?
              </h2>
              <p>
                Tell us your travel dates we&apos;ll design a tailor-made
                Lake Manyara safari with forest drives, lake viewpoints, and
                seamless Northern Circuit connections.
              </p>
              <a
                className="manyara-btn manyara-btn--primary manyara-btn--lg"
                href="#contact"
              >
                Get a free Manyara quote
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}

export default Manyara;
